import { useCallback, useEffect, useRef, useState } from "react"

import {useStripe, useElements, PaymentElement} from "@stripe/react-stripe-js";
import PropTypes from "prop-types"

import styles from "./checkout_form.module.scss"

import { fetchAuthenticityToken } from "app/scripts/refresh_form_authenticity_token"
import PromoCodeForm from "app/components/subscriptions/promo_code_form"
import Tooltip from "shared/components/tooltip"

import { logCustomDatadogAction } from "app/scripts/shared_setup"

const CheckoutForm = ({
  buttonConfig,
  email,
  onCreateSubscriptionStart,
  onCreateSubscriptionCancel,
  processedAfterPaymentMethodSetup,
  promoCodeConfig,
}) => {
  const stripe = useStripe()
  const elements = useElements()

  const [clientSecret, setClientSecret] = useState(null)
  const [returnUrl, setReturnUrl] = useState(null)
  const [handlingSubmit, setHandlingSubmit] = useState(false)
  const [isFormComplete, setIsFormComplete] = useState(false)
  const stripeLoadStartRef = useRef(null)

  useEffect(() => {
    fetchCreateIntent()
  }, [])

  const handleChange = (event) => {
    setIsFormComplete(event.complete && !event.error);
  };

  const fetchCreateIntent = async (maxAttempts = 3) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetch("/subscriptions/create_intent", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": await fetchAuthenticityToken(),
          },
        })

        if (!res.ok) throw new Error(`HTTP ${res.status}`)

        const result = await res.json()
        setClientSecret(result.clientSecret)
        setReturnUrl(result.returnUrl)
        return
      } catch (error) {
        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 250))
        } else {
          window.appendFlash("Something went wrong. Please reload the page and try again.", "danger")
        }
      }
    }
  }

  const openZendesk = (e) => {
    e.preventDefault()
    window.App.loadZendeskWidget()
  }

  const handleSubmit = async (event) => {
    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded. Make sure to disable
      // form submission until Stripe.js has loaded.
      setHandlingSubmit(false)
      return
    }

    await elements.submit().then(async result => {
      if (result.error) {
        window.appendFlash(result.error.message, "danger")
        setHandlingSubmit(false)
      } else if (result.selectedPaymentMethod) {
        onCreateSubscriptionStart(result.selectedPaymentMethod)

        stripe.confirmSetup({
          elements,
          clientSecret,
          confirmParams: {
            return_url: returnUrl,
            payment_method_data: {
              billing_details: {
                address: {
                  city: null,
                  country: null,
                  line1: null,
                  line2: null,
                  postal_code: null,
                  state: null
                },
              },
            },
          },
          redirect: "if_required"
        }).then(async result => {
          if (result.error) {
            onCreateSubscriptionCancel(result.error)
            setHandlingSubmit(false)
            return
          }

          const paymentMethodId = result.setupIntent.payment_method
          const checkoutFormReset = () => {
            setHandlingSubmit(false)
          }

          processedAfterPaymentMethodSetup(paymentMethodId, checkoutFormReset)
        })
      }
    })
  }

  const autoFocus = element => {
    if (!window.App.integrationTestMode) element.focus()
  }

  const buttonDisabled = !stripe || !elements || !clientSecret || handlingSubmit || !isFormComplete

  const loaderStartTrack = () => {
    stripeLoadStartRef.current = performance.now()
  }

  const readyTrack = (element) => {
    const stripeLoadTime = performance.now() - stripeLoadStartRef.current

    autoFocus(element)

    logCustomDatadogAction("stripe_load_completed", { loadTime: stripeLoadTime })
  }

  return (
    <form className={`form payment-info-form ${styles["payment-info-form"]}`} onSubmit={handleSubmit}>
      {elements && (
        <>
          <PaymentElement
            onChange={handleChange}
            onLoaderStart={loaderStartTrack}
            onReady={readyTrack}
            options={{
              fields: {
                billingDetails: {
                  address: "never",
                },
              },
              layout: {
                type: "tabs",
              },
              defaultValues: {
                billingDetails: {
                  email: email,
                },
              },
              terms: {
                card: "never"
              },
              wallets: {
                link: "never"
              }
            }}
          />

          {promoCodeConfig && (<PromoCodeForm {...promoCodeConfig} />)}
        </>
      )}

      <section className={`${styles["action-buttons"]} mt4 ${styles["mobile-top"]}`}>
        <button
          id="submit-payment"
          type="submit"
          disabled={buttonDisabled}
          className={buttonConfig.buttonType ? "button-secondary full-width" : "button-primary full-width"}
          title={buttonConfig.cta}
          aria-label={buttonConfig.cta}
          data-dd-action-name="Subscription credit card payment button"
        >
          {handlingSubmit ? buttonConfig.submittingCopy : buttonConfig.cta}
          {!handlingSubmit && (
            <Tooltip>
              Please be sure you’ve correctly completed the form. <br />
              <a className="link-inline" onClick={openZendesk}>
                Contact us
              </a>
              for help.
            </Tooltip>
          )}
        </button>
      </section>
    </form>
  )
}

CheckoutForm.propTypes = {
  buttonConfig: PropTypes.shape({
    buttonType: PropTypes.string,
    cta: PropTypes.string.isRequired,
    submittingCopy: PropTypes.string.isRequired,
  }),
  email: PropTypes.string.isRequired,
  onCreateSubscriptionCancel: PropTypes.func,
  onCreateSubscriptionStart: PropTypes.func,
  processedAfterPaymentMethodSetup: PropTypes.func.isRequired,
  promoCodeConfig: PropTypes.shape(),
}

export default CheckoutForm
